import {
  currentSub,
  getCSRFToken,
  isQueuePaused,
  pauseQueue,
  pauseQueueForPasswordChange,
  readLiveSessionUserId,
  refreshSession,
} from './auth'

const DB_NAME = 'tkf-hop'
const STORE_NAME = 'action-queue'
const DB_VERSION = 1

export type QueueStatus = 'pending' | 'sending' | 'sent' | 'conflict' | 'failed' | 'expired'

export interface QueueAction {
  uuid: string
  method: string
  url: string
  body?: string
  timestamp: number
  status: QueueStatus
  /** Delivery attempts so far. Absent on records written before this field existed. */
  attempts?: number
  /** Last HTTP status seen for this action, when there was one. */
  lastStatusCode?: number
  /** Set while `status` is `'sending'`; the moment this drainer claimed it. */
  claimedAt?: number
  /**
   * R413 (c): the id of the signed-in user who queued this write. Only that person's session
   * sends it. Absent on records queued before this field existed, or with no session user.
   */
  sub?: string
  /**
   * R517: a HELD write (a move tapped inside the Undo window). No drain sends or claims it
   * before this moment; once it has passed (or the field is cleared) it is an ordinary pending
   * write under its `sub`. Absent on every non-held write.
   */
  sendAfter?: number
}

export interface DrainOptions {
  /**
   * Also send items with no `sub`. Only the re-auth overlay passes this, and only when the
   * person re-authenticating is the one whose session the 401 paused (Rook #62940).
   */
  includeUnowned?: boolean
}

/** What one drain pass did with one action. */
export type DrainOutcome = 'sent' | 'conflict' | 'failed' | 'expired' | 'retry' | 'paused'

export interface DrainResult {
  uuid: string
  outcome: DrainOutcome
  statusCode?: number
}

/**
 * Bench §3.4 — an action older than this is NEVER sent.
 *
 * "AN ACTION QUEUED WEEKS AGO AGAINST A ROW THAT HAS SINCE MOVED IS NOT A
 *  RETRY, IT IS A TIME-TRAVELLING WRITE." — Rook #11470 ⑤
 *
 * This is the deploy-day guard: fixing the verb makes every historically failed
 * action drainable at once, in every operator browser. Anything older than a
 * day is reaped and reported instead of replayed against rows that have moved.
 */
export const MAX_ACTION_AGE_MS = 24 * 60 * 60 * 1000

/** Give up on a transient (5xx) failure after this many attempts. */
export const MAX_ATTEMPTS = 5

/**
 * bench-QUEUE-DRAINS-ON-RECONNECT.CB-E §1.1 — a claim this old is abandoned, not in-flight.
 * Mirrored in sw.js. If these two disagree, the SW re-sends what the page just claimed.
 */
export const CLAIM_STALE_MS = 60_000

/**
 * bench-QUEUE-DRAINS-ON-RECONNECT.CB-E §1.5 (Rook #42768) — a claim must not outlive its
 * send: the fetch is aborted at this timeout and the claim released, well before
 * CLAIM_STALE_MS could let the other drainer re-claim it while this one is still in flight.
 * Mirrored in sw.js. If these two disagree, two sends can be in flight for the same item.
 */
export const SEND_TIMEOUT_MS = 30_000

/** Methods the gateway's double-submit CSRF check applies to. */
const WRITE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])

let _db: Promise<IDBDatabase> | null = null

function openDb(): Promise<IDBDatabase> {
  if (_db) return _db
  _db = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = (e) => {
      const db = (e.target as IDBOpenDBRequest).result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'uuid' })
      }
    }
    req.onsuccess = (e) => resolve((e.target as IDBOpenDBRequest).result)
    req.onerror = (e) => reject((e.target as IDBOpenDBRequest).error)
  })
  return _db
}

export async function enqueue(method: string, url: string, body?: unknown): Promise<string> {
  return putNew(method, url, body)
}

async function putNew(method: string, url: string, body: unknown, sendAfter?: number): Promise<string> {
  const db = await openDb()
  const uuid = crypto.randomUUID()
  const item: QueueAction = {
    uuid,
    method,
    url,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    timestamp: Date.now(),
    status: 'pending',
    attempts: 0,
  }
  // R413 (c): stamp the owner; with no session user, no stamp.
  const sub = currentSub()
  if (sub) item.sub = sub
  if (sendAfter !== undefined) item.sendAfter = sendAfter
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite')
    const req = tx.objectStore(STORE_NAME).put(item)
    req.onsuccess = () => resolve()
    req.onerror = () => reject(req.error)
  })
  return uuid
}

/** A `'sending'` item whose claim is older than this is abandoned, not in-flight. */
function isStaleClaim(item: QueueAction, now: number): boolean {
  return item.status === 'sending' && now - (item.claimedAt ?? 0) > CLAIM_STALE_MS
}

/** R517: a held write whose `sendAfter` has not passed. No drain reads or claims it. Mirrored in sw.js. */
function isHeldNow(item: QueueAction, now: number): boolean {
  return typeof item.sendAfter === 'number' && item.sendAfter > now
}

/**
 * R517 (Frank #66769, Rook #66772): a move tapped inside the Undo window is written HERE at the
 * tap, stamped with the signed-in person like `enqueue`, and held until `sendAfter`. A closed
 * tab, a reload or a killed PWA leaves it for the next drain (same owner rule) once that passes.
 *
 * One owner for the send: while the screen is mounted, the SCREEN sends it at the window end,
 * after `takeHeld` consumes the entry; if `takeHeld` returns false a drain already claimed it
 * and the screen sends nothing.
 */
export async function enqueueHeld(method: string, url: string, body: unknown, holdMs: number): Promise<string> {
  return putNew(method, url, body, Date.now() + holdMs)
}

/** Runs `decide` on one item inside a single readwrite transaction. */
async function withItem(
  uuid: string,
  decide: (item: QueueAction | undefined, store: IDBObjectStore) => boolean,
): Promise<boolean> {
  const db = await openDb()
  return new Promise<boolean>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite')
    const store = tx.objectStore(STORE_NAME)
    const getReq = store.get(uuid)
    let result = false
    getReq.onsuccess = () => {
      result = decide(getReq.result as QueueAction | undefined, store)
    }
    getReq.onerror = () => reject(getReq.error)
    tx.oncomplete = () => resolve(result)
    tx.onerror = () => reject(tx.error)
  })
}

/**
 * R517: the screen's claim at the window end. Deletes the held entry, in one transaction, only
 * while it is still `'pending'` (no drain has claimed it). True: the caller sends, once. False: a
 * drain has it (or it is gone), and the caller sends nothing.
 */
export async function takeHeld(uuid: string): Promise<boolean> {
  return withItem(uuid, (item, store) => {
    if (!item || item.status !== 'pending') return false // a drain has it, or it is gone
    store.delete(uuid)
    return true
  })
}

/** R517: Undo. The held entry is deleted while it is still `'pending'`; nothing is sent. */
export async function cancelHeld(uuid: string): Promise<boolean> {
  return withItem(uuid, (item, store) => {
    if (!item || item.status !== 'pending') return false
    store.delete(uuid) // Undo: the held write is gone, nothing is sent
    return true
  })
}

/** R517: leaving the screen inside the window. `sendAfter` is cleared: an ordinary pending write. */
export async function releaseHeld(uuid: string): Promise<boolean> {
  return withItem(uuid, (item, store) => {
    if (!item || item.status !== 'pending' || item.sendAfter === undefined) return false
    const next: QueueAction = { ...item }
    delete next.sendAfter
    store.put(next)
    return true
  })
}

/** R517: a manual sign-out releases every held write of `sub` (they become ordinary pending writes). */
export async function releaseAllHeld(sub: string | null): Promise<number> {
  if (!sub) return 0
  const db = await openDb()
  return new Promise<number>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite')
    const store = tx.objectStore(STORE_NAME)
    const req = store.getAll()
    let released = 0
    req.onsuccess = () => {
      for (const item of req.result as QueueAction[]) {
        if (item.sub !== sub || item.status !== 'pending' || item.sendAfter === undefined) continue
        const next: QueueAction = { ...item }
        delete next.sendAfter
        store.put(next)
        released += 1
      }
    }
    req.onerror = () => reject(req.error)
    tx.oncomplete = () => resolve(released)
    tx.onerror = () => reject(tx.error)
  })
}

let kickRunning: Promise<void> | null = null
let kickAgain = false

/**
 * R517 (Frank #66783): after a release, drain now rather than at the next tick. One kicked drain
 * at a time: a kick while one runs asks for one more pass after it (that pass may not have read
 * the released entry). Other drainers (reconnect, the worker) are not serialised here; the claim
 * keeps them to one send per entry.
 */
export function kickDrain(): Promise<void> {
  if (kickRunning) {
    kickAgain = true
    return kickRunning
  }
  kickRunning = (async () => {
    try {
      do {
        kickAgain = false
        await drainQueue()
      } while (kickAgain)
    } catch {
      /* the entry stays pending for the next drain */
    } finally {
      kickRunning = null
    }
  })()
  return kickRunning
}

export async function getPending(now: number = Date.now()): Promise<QueueAction[]> {
  const db = await openDb()
  return new Promise<QueueAction[]>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly')
    const req = tx.objectStore(STORE_NAME).getAll()
    req.onsuccess = () =>
      resolve(
        (req.result as QueueAction[])
          .filter((i) => i.status === 'pending' || isStaleClaim(i, now))
          .filter((i) => !isHeldNow(i, now)) // R517: a held write waits for its sendAfter
          .sort((a, b) => a.timestamp - b.timestamp),
      )
    req.onerror = () => reject(req.error)
  })
}

/**
 * Claim one item for this drainer, in a single `readwrite` transaction: read it, decide, and
 * (if claimable) write `'sending'` back before the transaction can be interleaved with the
 * other drainer's own claim attempt on the same uuid. Only the drainer whose `claimItem` call
 * returns `true` may send — the page and the service worker never both send the same item.
 */
export async function claimItem(uuid: string, now: number): Promise<boolean> {
  const db = await openDb()
  return new Promise<boolean>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite')
    const store = tx.objectStore(STORE_NAME)
    const getReq = store.get(uuid)
    getReq.onsuccess = () => {
      const item = getReq.result as QueueAction | undefined
      if (!item || isHeldNow(item, now) || !(item.status === 'pending' || isStaleClaim(item, now))) {
        resolve(false)
        return
      }
      const putReq = store.put({ ...item, status: 'sending', claimedAt: now })
      putReq.onsuccess = () => resolve(true)
      putReq.onerror = () => reject(putReq.error)
    }
    getReq.onerror = () => reject(getReq.error)
  })
}

/**
 * Put a claimed item back to `'pending'` and clear its claim. Every outcome that leaves an
 * item for a later drain (401, a 5xx below the attempt cap, a network error or a send that
 * timed out) releases it this way — never left `'sending'` on purpose.
 */
async function releaseToPending(uuid: string, extra: Partial<QueueAction> = {}): Promise<void> {
  const db = await openDb()
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite')
    const store = tx.objectStore(STORE_NAME)
    const getReq = store.get(uuid)
    getReq.onsuccess = () => {
      const item = getReq.result as QueueAction | undefined
      if (item) {
        const next: QueueAction = { ...item, ...extra, status: 'pending' }
        delete next.claimedAt
        store.put(next)
      }
      resolve()
    }
    getReq.onerror = () => reject(getReq.error)
  })
}

async function patchItem(uuid: string, patch: Partial<QueueAction>): Promise<void> {
  const db = await openDb()
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite')
    const store = tx.objectStore(STORE_NAME)
    const getReq = store.get(uuid)
    getReq.onsuccess = () => {
      const item = getReq.result as QueueAction | undefined
      if (item) store.put({ ...item, ...patch })
      resolve()
    }
    getReq.onerror = () => reject(getReq.error)
  })
}

async function updateStatus(uuid: string, status: QueueStatus): Promise<void> {
  await patchItem(uuid, { status })
}

export async function markSent(uuid: string): Promise<void> {
  await updateStatus(uuid, 'sent')
}

/**
 * Drain the action queue sequentially, reporting what happened to each action.
 *
 * 200 → sent
 * 400 → conflict + hop:reconcile (D36 #6)
 * 401 → pause queue + hop:auth-required (D36 #7); queue must NOT be dropped
 * 403 PASSWORD_CHANGE_REQUIRED -> pause queue + hop:password-change-required, kept pending
 * 4xx → failed (terminal) + hop:action-failed   <- bench §3.3
 * 5xx → retried, counted, then failed at MAX_ATTEMPTS + hop:action-failed
 * stale → expired + hop:action-expired          <- bench §3.4
 * network error → left pending (offline is legitimate; see below)
 *
 * Bench §3.3 — THE SWALLOW. This used to end with
 *   `// Other non-OK statuses: leave pending for next drain`
 * which meant a 404 was retried forever, silently, in a queue nobody could see.
 * That is what hid the POST/PATCH verb mismatch. Every status now lands in a
 * named branch and every non-delivery raises an event, so the NEXT mismatch is
 * visible on the first drain instead of never.
 *
 * The return value exists so the UI can gate card removal on a landed write
 * (§3.2) rather than on the tap.
 */
export async function drainQueue(options: DrainOptions = {}): Promise<DrainResult[]> {
  const results: DrainResult[] = []
  if (isQueuePaused()) return results

  // R413 (c), Frank: on a shared device a write is sent only under the session of the person
  // who queued it. Read once; no signed-in user -> send nothing. Anyone else's item (and an
  // unowned one, unless the caller says otherwise) is skipped before the age gate and the
  // claim, so it stays pending, unclaimed, attempts unchanged.
  //
  // Two-tab binding (Frank #63294): the requests carry whatever HttpOnly cookie the
  // browser holds NOW, which another tab may have signed someone else into. Before
  // sending anything, the live session (GET /auth/session) must name the same person
  // this tab holds in memory; a failed read sends nothing (never fall back to memory).
  // The window between this read and the sends below stays open; closing it needs the
  // gateway. This check covers the common two-tab case, not that race.
  // When the live session names someone else, this tab adopts it (Rook #63300); a failed
  // read adopts nothing. The owner is taken once and used for the whole pass, including
  // the includeUnowned path. HOP has no retry-after-re-auth inside a pass: a 401 releases
  // the item, pauses and ends the pass, and the re-auth overlay starts a new drain, which
  // re-reads the live session here.
  const owner = currentSub()
  if (!owner) return results // nobody signed in: send nothing
  const live = await readLiveSessionUserId()
  if (live !== null && live !== owner) {
    await refreshSession() // another person's cookie: adopt their session, send nothing
    return results
  }
  if (live !== owner) return results // unknown (read failed or no session): send nothing

  const now = Date.now()
  const pending = (await getPending(now)).filter(
    (item) => item.sub === owner || (options.includeUnowned === true && item.sub === undefined),
  )

  for (const item of pending) {
    if (isQueuePaused()) {
      results.push({ uuid: item.uuid, outcome: 'paused' })
      break
    }

    // §3.4 — the age gate runs BEFORE the request, so a stale action is never
    // sent at all, rather than sent and then regretted.
    const ageMs = now - item.timestamp
    if (ageMs > MAX_ACTION_AGE_MS) {
      await patchItem(item.uuid, { status: 'expired' })
      window.dispatchEvent(
        new CustomEvent('hop:action-expired', {
          detail: { uuid: item.uuid, url: item.url, method: item.method, ageMs },
        }),
      )
      results.push({ uuid: item.uuid, outcome: 'expired' })
      continue
    }

    // bench-QUEUE-DRAINS-ON-RECONNECT.CB-E §1.1 — only the drainer whose claim lands may
    // send. The other sees this item as already claimed (and not stale) and moves on.
    if (!(await claimItem(item.uuid, now))) continue

    // Cookie carries the session automatically (same-origin); the gateway's double-submit
    // CSRF check still needs the header echoed back on every state-changing request.
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (WRITE_METHODS.has(item.method.toUpperCase())) {
      headers['X-XSRF-TOKEN'] = getCSRFToken()
    }

    // §1.5 (Rook #42768) — a claim must not outlive its send.
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), SEND_TIMEOUT_MS)

    try {
      const res = await fetch(item.url, {
        method: item.method,
        headers,
        body: item.body,
        signal: controller.signal,
      })

      if (res.ok) {
        await patchItem(item.uuid, { status: 'sent', lastStatusCode: res.status })
        results.push({ uuid: item.uuid, outcome: 'sent', statusCode: res.status })
      } else if (res.status === 400) {
        await patchItem(item.uuid, { status: 'conflict', lastStatusCode: 400 })
        window.dispatchEvent(new CustomEvent('hop:reconcile', { detail: { uuid: item.uuid } }))
        results.push({ uuid: item.uuid, outcome: 'conflict', statusCode: 400 })
      } else if (res.status === 401) {
        await releaseToPending(item.uuid)
        pauseQueue()
        results.push({ uuid: item.uuid, outcome: 'paused', statusCode: 401 })
        break
      } else if (res.status === 403 && (await isPasswordChangeRequired(res))) {
        // R413 (c): the master layer refuses a flagged person on every write until they
        // choose their own password. That is not a terminal failure of this action: keep it,
        // pause like a 401, and send the person to the change-password screen. Any other 403
        // (e.g. the gateway's CSRF refusal) falls through to the terminal branch below.
        await releaseToPending(item.uuid)
        pauseQueueForPasswordChange()
        results.push({ uuid: item.uuid, outcome: 'paused', statusCode: 403 })
        break
      } else {
        // §3.3 — the branch that used to be a comment.
        // A 4xx will not become a 2xx by being retried: that is terminal, and
        // retrying it forever is precisely the bug. A 5xx may be transient, so
        // it is retried a bounded number of times and counted either way.
        const attempts = (item.attempts ?? 0) + 1
        const terminal = res.status < 500 || attempts >= MAX_ATTEMPTS
        if (terminal) {
          await patchItem(item.uuid, { status: 'failed', attempts, lastStatusCode: res.status })
        } else {
          await releaseToPending(item.uuid, { attempts, lastStatusCode: res.status })
        }
        window.dispatchEvent(
          new CustomEvent('hop:action-failed', {
            detail: {
              uuid: item.uuid,
              url: item.url,
              method: item.method,
              status: res.status,
              attempts,
              terminal,
            },
          }),
        )
        results.push({
          uuid: item.uuid,
          outcome: terminal ? 'failed' : 'retry',
          statusCode: res.status,
        })
      }
    } catch {
      // Network error (offline is legitimate) or the SEND_TIMEOUT_MS abort above: either
      // way the request never landed, and the claim must not outlive it. Attempts are
      // recorded for visibility but deliberately do NOT count toward MAX_ATTEMPTS:
      // expiring an offline operator's action after five background ticks would be a new
      // swallow. Staleness is bounded by MAX_ACTION_AGE_MS instead, which is the right
      // axis for "offline too long".
      await releaseToPending(item.uuid, { attempts: (item.attempts ?? 0) + 1 })
      results.push({ uuid: item.uuid, outcome: 'retry' })
    } finally {
      clearTimeout(timer)
    }
  }

  return results
}

/** Reset module state — test use only. */
export function _resetForTest(): void {
  _db = null
}

/**
 * R413 (c): true only for the master layer's 403 body { error: { code:
 * 'PASSWORD_CHANGE_REQUIRED' } }. Reads a clone; no clone, a non-JSON body or the gateway's
 * string error ({ error: 'csrf_validation_failed' }) all read as false.
 */
async function isPasswordChangeRequired(res: Response): Promise<boolean> {
  try {
    if (typeof res.clone !== 'function') return false
    const body = (await res.clone().json()) as { error?: { code?: unknown } | string } | null
    const error = body?.error
    return typeof error === 'object' && error !== null && error.code === 'PASSWORD_CHANGE_REQUIRED'
  } catch {
    return false
  }
}
